/** Shared historical-research chrome from the Options mockup.
 * Visual pattern only. Live callers must pass ranked/backtest values, not mock premiums.
 */
import { formatHistoricalScalarDisplay } from "@/workspace/live/workspaceLiveAdapter";
import { LIVE_UNAVAILABLE } from "@/workspace/live/workspaceLiveUnavailable";

export function NeutralStat({ label, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
      <div className="text-[12px] font-mono-data mt-0.5">{children}</div>
    </div>
  );
}

export function pct(v) {
  return v == null || !Number.isFinite(v) ? "Not available in this output" : `${v.toFixed(1)}%`;
}

export function ror(v) {
  if (v == null || !Number.isFinite(v)) return "Not available in this output";
  const n = v * 100;
  return `${n >= 0 ? "+" : "−"}${Math.abs(n).toFixed(1)}%`;
}

export function usd(v) {
  if (v == null || !Number.isFinite(v)) return "Not available in this output";
  return `$${Math.abs(v).toLocaleString()}`;
}

function mockCell(v, kind) {
  if (v == null || v === undefined) return "Not available in this output";
  if (kind === "pct") return pct(v);
  if (kind === "ror") return ror(v);
  if (kind === "usd") return usd(v);
  return String(v);
}

export function liveMetricValue(value) {
  const shown = formatHistoricalScalarDisplay(value);
  return shown == null ? LIVE_UNAVAILABLE.notInOutput : shown;
}

export function RegimeContextTable({ columns, confirmedState, valueMode = "mock", testid = "regime-context-table" }) {
  const live = valueMode === "live";
  const showAssign = (columns || []).some((c) => c.assignmentSupported);
  const list = Array.isArray(columns) ? columns : [];
  if (!list.length) {
    return (
      <p className="text-[11px]" style={{ color: "var(--hoip-text-secondary)" }} data-testid={testid}>
        {LIVE_UNAVAILABLE.researchContextUnavailable}
      </p>
    );
  }
  return (
    <div className="overflow-x-auto min-w-0" data-testid={testid}>
      <table className="w-full text-[10px]">
        <thead>
          <tr className="text-left uppercase tracking-wide text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            <th className="py-1 pr-2 font-medium">Metric</th>
            {list.map((c) => (
              <th
                key={c.key}
                className="py-1 pr-2 font-medium"
                style={c.current ? { color: "var(--hoip-text-primary)", background: "rgba(59,130,246,0.10)" } : undefined}
              >
                {c.label}{c.current ? " · now" : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {[
            { label: "Historical win rate", key: "win_rate", kind: "pct" },
            { label: "Average return on risk", key: "avg_return_on_risk", kind: "ror" },
            { label: "Sample count", key: "sample_size", kind: "n" },
            showAssign ? { label: "Assignment rate", key: "assignment_rate", kind: "pct" } : null,
            { label: "Maximum historical loss", key: "max_loss", kind: "usd" },
          ].filter(Boolean).map((row) => (
            <tr key={row.key} className="border-t" style={{ borderColor: "var(--hoip-divider)" }}>
              <td className="py-1 pr-2" style={{ color: "var(--hoip-text-tertiary)" }}>{row.label}</td>
              {list.map((c) => {
                const insufficient = c.insufficient && row.key !== "sample_size" && row.key !== "max_loss";
                const style = c.current ? { background: "rgba(59,130,246,0.10)" } : undefined;
                if (insufficient) {
                  return (
                    <td key={`${c.key}-${row.key}`} className="py-1 pr-2 font-mono-data align-top" style={style}>
                      n&lt;20
                    </td>
                  );
                }
                let value;
                if (row.key === "sample_size") {
                  value = c.sample_size == null
                    ? (live ? LIVE_UNAVAILABLE.notInOutput : "Not available in this output")
                    : c.sample_size;
                } else {
                  value = live ? liveMetricValue(c[row.key]) : mockCell(c[row.key], row.kind);
                }
                return (
                  <td key={`${c.key}-${row.key}`} className="py-1 pr-2 font-mono-data align-top" style={style}>
                    {value}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {confirmedState ? (
        <p className="text-[10px] mt-1.5" style={{ color: "var(--hoip-text-tertiary)" }}>
          Current confirmed regime: {confirmedState}. Historical performance is visually neutral and is not a forecast.
        </p>
      ) : null}
    </div>
  );
}
